import { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const DONATION_OPTIONS = {
  cashapp: {
    name: 'CashApp',
    handle: '$t3b3tu',
    link: 'https://cash.app/$t3b3tu',
    color: 'bg-[#00D632]',
    icon: '💵'
  },
  bitcoin: {
    name: 'Bitcoin',
    address: '1LNrLrCMZ7UuQH16JCWjiJ6GWWhgfo8iXU',
    color: 'bg-[#F7931A]',
    icon: '₿'
  }
};

export const TipJar = ({ compact = false }) => {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(null), 2000);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 flex-wrap justify-center">
        <span className="text-slate-400 text-sm flex items-center gap-1">
          <Heart size={14} className="text-red-400" />
          Support:
        </span>
        <a
          href={DONATION_OPTIONS.cashapp.link}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 rounded-full bg-[#00D632]/20 text-[#00D632] text-sm hover:bg-[#00D632]/30 transition-colors"
          data-testid="cashapp-link-compact"
        >
          💵 CashApp
        </a>
        <button
          onClick={() => copyToClipboard(DONATION_OPTIONS.bitcoin.address, 'btc')}
          className="px-3 py-1.5 rounded-full bg-[#F7931A]/20 text-[#F7931A] text-sm hover:bg-[#F7931A]/30 transition-colors flex items-center gap-1"
          data-testid="bitcoin-copy-compact"
        >
          ₿ Bitcoin
          {copied === 'btc' ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass-card p-6 text-center"
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <Heart className="w-5 h-5 text-red-400" />
        <h3 className="font-heading text-xl text-white">Support the Cosmos</h3>
      </div>
      <p className="text-slate-400 text-sm mb-6">
        If you enjoyed your reading, consider leaving a tip to support future cosmic insights!
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        {/* CashApp */}
        <a
          href={DONATION_OPTIONS.cashapp.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#00D632]/20 border border-[#00D632]/30 text-[#00D632] hover:bg-[#00D632]/30 transition-all group"
          data-testid="cashapp-link"
        >
          <span className="text-xl">💵</span>
          <span className="font-medium">CashApp</span>
          <ExternalLink size={16} className="opacity-50 group-hover:opacity-100" />
        </a>

        {/* Bitcoin */}
        <button
          onClick={() => copyToClipboard(DONATION_OPTIONS.bitcoin.address, 'bitcoin')}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#F7931A]/20 border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/30 transition-all"
          data-testid="bitcoin-copy"
        >
          <span className="text-xl">₿</span>
          <span className="font-medium">Bitcoin</span>
          {copied === 'bitcoin' ? (
            <Check size={16} />
          ) : (
            <Copy size={16} className="opacity-50" />
          )}
        </button>
      </div>

      {/* Bitcoin address display */}
      <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/10">
        <p className="text-xs text-slate-500 mb-1">Bitcoin Address</p>
        <p className="text-xs font-mono text-slate-300 break-all">
          {DONATION_OPTIONS.bitcoin.address}
        </p>
      </div>
    </motion.div>
  );
};

export const TipJarInline = () => {
  const [copied, setCopied] = useState(false);

  const copyBitcoin = () => {
    navigator.clipboard.writeText(DONATION_OPTIONS.bitcoin.address);
    setCopied(true);
    toast.success('Bitcoin address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-4 flex-wrap justify-center py-4">
      <span className="text-slate-400 text-sm flex items-center gap-1">
        <Heart size={14} className="text-red-400 animate-pulse" />
        Enjoyed your reading?
      </span>
      <div className="flex gap-2">
        <a
          href={DONATION_OPTIONS.cashapp.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tip-cashapp"
        >
          <Button size="sm" className="bg-[#00D632] hover:bg-[#00D632]/80 text-white gap-1">
            💵 Tip via CashApp
          </Button>
        </a>
        <Button
          size="sm"
          variant="outline"
          onClick={copyBitcoin}
          className="border-[#F7931A]/50 text-[#F7931A] hover:bg-[#F7931A]/20 gap-1"
          data-testid="tip-bitcoin"
        >
          ₿ {copied ? 'Copied!' : 'Bitcoin'}
        </Button>
      </div>
    </div>
  );
};
